import apiService from "./services/api";
import { buildBrandManagerCards } from "./lib/brandManagerStore";
import { isBrandSupportPath } from "./supportHubPaths";

/**
 * Support email deep links carry `?brandId=` so a Brand Case opens inside the
 * right Brand context. The URL is never trusted as authorization: the Brand is
 * honoured only if it is one of the acting user's authorized Brands (active
 * UserBrandAccess membership present in the Super Admin catalog) — the same
 * source the Brand Manager cards are built from.
 *
 * Returns { id, name, logoUrl, type } or null (unknown / not authorized).
 */
export async function resolveAuthorizedBrandById(brandId) {
  const id = String(brandId || "").trim();
  if (!id) return null;
  try {
    const [mineRaw, catalogRaw] = await Promise.all([
      apiService.listMyBrands().catch(() => ({ brandMemberships: [] })),
      apiService.listBrandCatalog().catch(() => ({ brands: [] })),
    ]);
    const mine = mineRaw?.data ?? mineRaw;
    const catalog = catalogRaw?.data ?? catalogRaw;
    const cards = buildBrandManagerCards({
      catalogBrands: catalog?.brands || catalog?.items || [],
      memberships: mine?.brandMemberships || [],
    });
    const card = cards.find((c) => String(c.id) === id);
    if (!card) return null;
    return {
      id: card.id,
      name: card.name,
      logoUrl: card.logoUrl || null,
      type: card.type || null,
    };
  } catch {
    return null;
  }
}

/**
 * Pre-login deep-link preservation. ProtectedRoute stashes the in-app
 * location the user tried to reach; AuthCallback consumes it after a
 * successful login. Only same-origin, in-app relative paths are ever
 * honoured (never protocol-relative or absolute URLs).
 */
export const POST_LOGIN_RETURN_TO_KEY = "kk_post_login_return_to";

export function isSafeReturnPath(path) {
  const p = String(path || "");
  if (!p.startsWith("/")) return false;
  if (p.startsWith("//") || p.startsWith("/\\")) return false;
  if (/^\/(signin|signup|callback|support-mode|password-changed|forgot-password|confirm-email|vendor-signup)(\/|\?|#|$)/.test(p)) {
    return false;
  }
  if (p === "/" || p === "/app") return false;
  return true;
}

/**
 * Explicit return-to classes. Login wipes the selected Brand, so only paths
 * that render correctly with NO pre-existing Brand context are restorable:
 *   - "portfolio": /brand-manager and its pages (global Client context)
 *   - "brand-support": /master-account/support — re-establishes the Brand
 *     only via an authorized ?brandId= (resolveAuthorizedBrandById), else
 *     fails closed to the Client Support Hub
 * Anything else (e.g. bare /master-account/user-management) is not restored;
 * login falls through to /app → the tenant's normal landing.
 */
export function classifyReturnPath(path) {
  if (!isSafeReturnPath(path)) return null;
  const pathname = String(path).split(/[?#]/)[0].replace(/\/$/, "");
  if (pathname === "/brand-manager" || pathname.startsWith("/brand-manager/")) {
    return "portfolio";
  }
  if (isBrandSupportPath(pathname)) return "brand-support";
  return null;
}

export function isRestorableReturnPath(path) {
  return classifyReturnPath(path) !== null;
}

export function rememberPostLoginReturnTo(locationLike, storage = defaultStorage()) {
  if (!storage) return;
  const path =
    typeof locationLike === "string"
      ? locationLike
      : `${locationLike?.pathname || ""}${locationLike?.search || ""}${locationLike?.hash || ""}`;
  try {
    if (isRestorableReturnPath(path)) {
      storage.setItem(POST_LOGIN_RETURN_TO_KEY, path);
    } else {
      // The latest attempted route wins; never resume an older, unrelated one.
      storage.removeItem(POST_LOGIN_RETURN_TO_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function consumePostLoginReturnTo(storage = defaultStorage()) {
  if (!storage) return null;
  try {
    const path = storage.getItem(POST_LOGIN_RETURN_TO_KEY);
    storage.removeItem(POST_LOGIN_RETURN_TO_KEY);
    return isRestorableReturnPath(path) ? path : null;
  } catch {
    return null;
  }
}

export function clearPostLoginReturnTo(storage = defaultStorage()) {
  try {
    storage?.removeItem(POST_LOGIN_RETURN_TO_KEY);
  } catch {
    /* ignore */
  }
}

function defaultStorage() {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage : null;
  } catch {
    return null;
  }
}
